/**
 * UTM parameter helpers for the shared app link system.
 *
 * These utilities are framework-agnostic string helpers so they can be unit
 * tested without React. The React-facing pieces live in `LinkProvider` and the
 * `AppLink` component (in the `ui` entry point).
 */

/** Standard UTM query parameters supported by the link system. */
export interface UtmParams {
  /** utm_source — where the traffic originates (e.g. "newsletter"). */
  source?: string;
  /** utm_medium — the marketing medium (e.g. "email", "social"). */
  medium?: string;
  /** utm_campaign — the specific campaign name (e.g. "spring_launch"). */
  campaign?: string;
  /** utm_term — paid keyword term. */
  term?: string;
  /** utm_content — used to differentiate creatives/links in the same campaign. */
  content?: string;
}

/** Maps the friendly {@link UtmParams} keys onto their `utm_*` query names. */
const UTM_KEY_MAP: Record<keyof UtmParams, string> = {
  source: 'utm_source',
  medium: 'utm_medium',
  campaign: 'utm_campaign',
  term: 'utm_term',
  content: 'utm_content',
};

/**
 * Merge multiple partial {@link UtmParams} objects. Later objects win, and
 * `undefined`/empty values never overwrite an already-set value.
 */
export function mergeUtmParams(...sources: (UtmParams | undefined)[]): UtmParams {
  const result: UtmParams = {};
  for (const source of sources) {
    if (!source) continue;
    (Object.keys(source) as (keyof UtmParams)[]).forEach((key) => {
      const value = source[key];
      if (value !== undefined && value !== null && value !== '') {
        result[key] = value;
      }
    });
  }
  return result;
}

/**
 * Determine whether an href points somewhere outside the app's client-side
 * router. Absolute URLs (http/https), protocol-relative URLs, and non-http
 * schemes (mailto:, tel:, etc.) are all treated as external.
 */
export function isExternalHref(href: string): boolean {
  if (!href) return false;
  // Protocol-relative (//example.com) or absolute with a scheme.
  if (href.startsWith('//')) return true;
  // Anything with a scheme like `https:`, `mailto:`, `tel:`, `ftp:`.
  return /^[a-z][a-z0-9+.-]*:/i.test(href);
}

/**
 * Non-http(s) schemes that must never receive UTM query params (e.g. mailto,
 * tel, sms). Appending query strings to these is meaningless or breaks them.
 */
export function isNonHttpScheme(href: string): boolean {
  if (!href) return false;
  const match = href.match(/^([a-z][a-z0-9+.-]*):/i);
  if (!match) return false;
  const scheme = match[1].toLowerCase();
  return scheme !== 'http' && scheme !== 'https';
}

/**
 * Append UTM parameters to a string href, preserving any existing query string
 * and hash fragment. Existing UTM params already on the href are respected and
 * NOT overwritten, so a caller can hard-code a UTM value inline if needed.
 *
 * Returns the href unchanged when there are no params to add or when the href
 * uses a non-http scheme (mailto:, tel:, etc.).
 */
export function appendUtmToHref(href: string, utm: UtmParams): string {
  const entries = (Object.keys(utm) as (keyof UtmParams)[])
    .map((key) => [UTM_KEY_MAP[key], utm[key]] as const)
    .filter(([, value]) => value !== undefined && value !== null && value !== '');

  if (entries.length === 0) return href;
  if (isNonHttpScheme(href)) return href;

  // Split off the hash so params are inserted before it.
  const hashIndex = href.indexOf('#');
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : '';
  const base = hashIndex >= 0 ? href.slice(0, hashIndex) : href;

  const queryIndex = base.indexOf('?');
  const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base;
  const search = new URLSearchParams(queryIndex >= 0 ? base.slice(queryIndex + 1) : '');

  for (const [key, value] of entries) {
    // Respect any UTM value already present on the href.
    if (!search.has(key)) {
      search.set(key, String(value));
    }
  }

  const queryString = search.toString();
  return `${path}${queryString ? `?${queryString}` : ''}${hash}`;
}
